import React, { useEffect, useState, useRef } from 'react';
import {
  DatePicker,
  Select,
  Button,
  Form,
  Cascader,
  Input,
  Upload,
  message,
  Radio,
  TreeSelect,
  Checkbox,
  Switch,
  FormInstance,
  Row,
  Col,
  Grid,
  Space,
} from 'antd';
import 'moment/locale/zh-cn';
import locale from 'antd/es/date-picker/locale/zh_CN';
import { cloneDeep } from 'lodash';
import { filterOption } from './util';
import type { FieldItem, FormConfig } from './formConfig';
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

/**
 * 下拉/日期浮层挂载到抽屉根容器。
 * 右侧抽屉层级高于 body 下默认 antd 浮层，若使用默认挂载点会被抽屉遮住；
 * 也不挂到表单项父级，避免浮层打开/关闭改变表单项尺寸时触发 ResizeObserver loop。
 */
const getDrawerPopupContainer = (triggerNode: HTMLElement) =>
  (triggerNode.closest('.map-editor-drawer-container') as HTMLElement | null) ??
  triggerNode.parentElement ??
  triggerNode;

export interface FormProps {
  formConfig: FormConfig;
  layout?: 'horizontal' | 'vertical' | 'inline';
  defaultValue?: {
    [key in string]: any;
  };
  colon?: boolean;
  columnNum?: number; // 表单有多少列
  className?: string;
  labelAlign?: 'left' | 'right' | undefined;
  updateForm?: any;
  onValueChange?: Function;
  getFormInstance?: Function;
}
const CommonForm = (props: FormProps) => {
  const {
    formConfig,
    layout,
    labelAlign,
    className,
    colon,
    columnNum,
    defaultValue = {},
    onValueChange,
    getFormInstance,
  } = props;
  const [formData, setFormData] = useState<any>(cloneDeep(formConfig));
  const [commonFormRef] = Form.useForm();
  useEffect(() => {
    setFormData(cloneDeep(formConfig));
  }, [formConfig]);

  useEffect(() => {
    for (const f of formConfig['fields']) {
      const fieldName = f.fieldName as any;
      onFieldsChange(fieldName, defaultValue[fieldName]);
    }
    commonFormRef.setFieldsValue(defaultValue);
    // console.log(defaultValue)
  }, [defaultValue]);

  useEffect(() => {
    getFormInstance && getFormInstance(commonFormRef);
  }, []);

  const renderFieldItem = (dataItem: FieldItem) => {
    const {
      type,
      options,
      placeholder,
      multiple,
      validatorRules = [],
      appendixType = '.jpg, .jpeg, .png',
      max = 5,
      showSearch,
      maxLength,
      allowClear,
      changeOnSelect,
      disabled,
      showSelectAll,
      autoSize,
      showCount,
      maxTagCount,
      fieldRender,
      defaultChecked,
      clstag,
    } = dataItem;
    switch (type) {
      case 'input':
        return (
          <Input
            placeholder={placeholder ?? ''}
            allowClear={typeof allowClear === 'undefined' ? true : allowClear}
            maxLength={maxLength}
            clstag={clstag}
          />
        );
      case 'radioGroup':
        return <Radio.Group options={options} />;
      case 'textarea':
        return (
          <Input.TextArea
            placeholder={placeholder ?? ''}
            autoSize={autoSize === true ? { minRows: 2, maxRows: 6 } : autoSize}
            maxLength={maxLength}
            showCount={
              showCount && {
                formatter: ({ value, count, maxLength }) =>
                  `${count}/${maxLength}`,
              }
            }
          />
        );
      case 'select':
        return (
          <Select
            placeholder={placeholder ?? ''}
            options={options ?? []}
            showSearch={showSearch}
            mode={multiple ? 'multiple' : undefined}
            allowClear={typeof allowClear === 'undefined' ? true : allowClear}
            filterOption={showSearch && filterOption}
            getPopupContainer={getDrawerPopupContainer}
            {...(clstag ? ({ clstag } as any) : {})}
          />
        );
      case 'rangeTime':
        return (
          <RangePicker
            locale={locale}
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            getPopupContainer={getDrawerPopupContainer}
            // disabledDate={disabledDate}
          />
        );
      case 'dateTime':
        return (
          <DatePicker
            allowClear={typeof allowClear === 'undefined' ? true : allowClear}
            locale={locale}
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            disabled={disabled}
            getPopupContainer={getDrawerPopupContainer}
          />
        );
      case 'cascader':
        return (
          <Cascader
            options={options}
            placeholder={placeholder ?? ''}
            maxTagCount={maxTagCount}
            showSearch={showSearch}
            allowClear={typeof allowClear === 'undefined' ? true : allowClear}
            multiple={multiple}
            getPopupContainer={getDrawerPopupContainer}
            showCheckedStrategy={Cascader.SHOW_CHILD}
            dropdownRender={(menus) => {
              return (
                <>
                  {showSelectAll && (
                    <Checkbox className="select-all">全部</Checkbox>
                  )}
                  {menus}
                </>
              );
            }}
          />
        );
      case 'switch':
        return <Switch defaultChecked={defaultChecked} />;
      case 'customize':
        return fieldRender;
      default:
        return null;
    }
  };

  const onFieldsChange = (changedFieldName: any, val: any) => {
    if (!formData?.linkRules) {
      return;
    }
    // 找到当前元素改变后需要的联动规则
    const rule = formData?.linkRules[changedFieldName];
    if (!rule) {
      return;
    }
    rule.forEach((item: any) => {
      const { linkFieldName, rule, dependenceData, disabledValue } = item;
      const needChangeField: FieldItem = formData.fields?.find(
        (field: FieldItem) => field.fieldName === linkFieldName
      );
      const name = needChangeField.fieldName as any;
      switch (rule) {
        case 'visible':
          needChangeField.hidden = !dependenceData.includes(val);
          break;
        case 'dataEcho':
          commonFormRef.setFieldsValue({
            [name]: 'huixianla' + name,
          });
          break;
        case 'refresh':
          commonFormRef.setFieldsValue({
            [name]: defaultValue[name],
          });
          break;
        case 'valueDisable':
          if (needChangeField.options) {
            needChangeField.options = needChangeField.options.map((v: any) => {
              if (
                disabledValue.includes(v.value) &&
                dependenceData.includes(val)
              ) {
                return {
                  ...v,
                  disabled: true,
                };
              } else {
                return { ...v, disabled: false };
              }
            });
          }
          break;
      }
    });
    setFormData({ ...formData });
  };

  const calculateColSpan = (item: any) => {
    let span = columnNum ? Math.floor(24 / columnNum) : 8;
    if (item.colSpan && item.colSpan > 0) {
      span = span * item.colSpan;
    }
    return span;
  };

  return (
    <div className="common-form-container">
      <Form
        name="complex-form"
        labelAlign={labelAlign ?? 'right'}
        layout={layout ?? 'horizontal'}
        colon={colon ?? true}
        form={commonFormRef}
        autoComplete="off"
        className={className || ''}
        style={{
          alignItems: 'center',
        }}
        onFieldsChange={(changedFields: any) => {
          const changedFieldName = changedFields[0].name[0];
          const formValues = commonFormRef.getFieldsValue();
          onValueChange && onValueChange(formValues);
          changedFieldName &&
            onFieldsChange(changedFieldName, formValues[changedFieldName]);
        }}
      >
        <Row gutter={20} align="middle">
          {formData?.fields?.map((item: any, index: number) => {
            return (
              item.hidden !== 'undefined' &&
              !item.hidden && (
                <Col
                  span={calculateColSpan(item)}
                  key={item.fieldName.toString()}
                >
                  <Form.Item
                    labelCol={item.labelCol || { span: 8 }}
                    wrapperCol={
                      item.wrapperCol ||
                      (layout === 'vertical' ? { span: 24 } : { span: 16 })
                    }
                    name={item.fieldName}
                    label={item.label}
                    help={item.help}
                    rules={item.validatorRules ?? []}
                    className={'field_' + item.fieldName.toString()}
                  >
                    {renderFieldItem(item)}
                  </Form.Item>
                </Col>
              )
            );
          })}
        </Row>
      </Form>
    </div>
  );
};

export default React.memo(CommonForm);
